import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { useLanguage, isHebrewPath, HE_PREFIX } from "@/contexts/LanguageContext";
import { PUBLIC_ROUTES, SITE, canonicalUrl, type SeoKey } from "@/config/seo";
import { FAQ_ITEMS } from "@/components/flowly/Landing/FAQSection/constants";
import { HELP_FAQ_ITEMS } from "@/pages/Help/constants";
import { HOW_IT_WORKS_STEPS } from "@/components/flowly/Landing/HowItWorks/constants";
import { PLANS } from "@/config/plans";
import { COMPARISONS } from "@/pages/Compare/data";

/** Robots directive for indexable pages — allows rich image/snippet previews. */
const PUBLIC_ROBOTS =
  "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1";

/** seoKeys that represent generative-AI "flow" pages — each gets its own
 *  SoftwareApplication entry so AI engines can extract per-flow metadata
 *  (name, description, URL, category) instead of falling back to the
 *  site-wide SoftwareApplication in index.html. Pricing + Help + legal
 *  pages are excluded because they're not "use the tool" surfaces. */
const FLOW_SEO_KEYS: ReadonlySet<SeoKey> = new Set<SeoKey>([
  "productCatalog",
  "productCreative",
  "imageGenerator",
  "profilePhoto",
  "talkingAvatar",
  "baseballTrend",
  "textToVideo",
  "framesToVideo",
  "imageDirector",
]);

/** Build a schema.org FAQPage object from resolved question/answer pairs. */
function faqPageJsonLd(pairs: { question: string; answer: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: pairs.map(({ question, answer }) => ({
      "@type": "Question",
      name: question,
      acceptedAnswer: { "@type": "Answer", text: answer },
    })),
  };
}

/** Per-flow SoftwareApplication — each tool gets its own entity so AI
 *  engines and Google can match a flow page to a specific app, not to the
 *  parent brand only. */
function softwareApplicationJsonLd(args: {
  name: string;
  description: string;
  url: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: args.name,
    description: args.description,
    url: args.url,
    applicationCategory: "MultimediaApplication",
    operatingSystem: "Web",
    publisher: { "@id": SITE.orgId },
    offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
  };
}

/** Pricing-page Product list — one Product per plan with its monthly Offer.
 *  Drives Google rich results for "[brand] pricing" + lets AI engines quote
 *  exact prices when summarising plan comparisons. */
function pricingProductListJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: PLANS.filter((p) => p.monthlyPriceNIS).map((plan, idx) => ({
      "@type": "ListItem",
      position: idx + 1,
      item: {
        "@type": "Product",
        name: `TodoAI — ${plan.name}`,
        description: `${plan.tokens.toLocaleString()} credits per month`,
        brand: { "@id": SITE.orgId },
        offers: {
          "@type": "Offer",
          price: plan.monthlyPriceNIS,
          priceCurrency: "ILS",
          url: `${SITE.baseUrl}/pricing`,
          availability: "https://schema.org/InStock",
        },
      },
    })),
  };
}

/** Breadcrumb trail — emits "Home › [Page]" for every non-home route. Helps
 *  Google show a clean URL hierarchy in search results and gives AI engines
 *  the site's information architecture in a single payload. */
function breadcrumbJsonLd(currentTitle: string, currentUrl: string) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: SITE.brand,
        item: `${SITE.baseUrl}/`,
      },
      {
        "@type": "ListItem",
        position: 2,
        name: currentTitle,
        item: currentUrl,
      },
    ],
  };
}

/**
 * Per-route SEO head tags — the single mechanism for page-level metadata.
 *
 * Rendered once inside `Layout`. It reads the current route and the active
 * language, looks the route up in `PUBLIC_ROUTES` (src/config/seo.ts), and
 * emits a unique <title>, meta description, robots directive, self-referencing
 * canonical, Open Graph / Twitter tags and — on `/` and `/help` — JSON-LD.
 *
 * Routes absent from `PUBLIC_ROUTES` (gated pages, the NotFound page, any
 * unmapped path) are marked `noindex` so they stay out of search results.
 * Adding SEO for a new page is one entry in `PUBLIC_ROUTES` plus its
 * `seo.<key>` strings in translations.
 */
export function RouteSEO() {
  const { pathname } = useLocation();
  const { t, language } = useLanguage();

  // Strip the Hebrew prefix to find the underlying PUBLIC_ROUTES entry —
  // /he/pricing and /pricing both map to the same seoKey ("pricing") and
  // the same content; they differ only in which prerendered HTML the
  // crawler receives.
  const onHebrewUrl = isHebrewPath(pathname);
  const unprefixedPath = onHebrewUrl
    ? pathname.slice(HE_PREFIX.length) || "/"
    : pathname;

  const route = PUBLIC_ROUTES.find((r) => r.path === unprefixedPath);

  // Non-public route — keep it out of the index; index.html static tags remain.
  if (!route) {
    return (
      <Helmet>
        <meta name="robots" content="noindex, follow" />
      </Helmet>
    );
  }

  const { title, description } = t.seo[route.seoKey];
  // Canonical for THIS request — points to the language variant the user
  // is currently on. The hreflang alternates below tell Google about the
  // sibling URL so it can pick the right one per searcher's locale.
  const enUrl = canonicalUrl(route.path);
  const heUrl =
    route.path === "/"
      ? `${SITE.baseUrl}${HE_PREFIX}`
      : `${SITE.baseUrl}${HE_PREFIX}${route.path}`;
  const url = onHebrewUrl ? heUrl : enUrl;
  const ogLocale = language === "he" ? "he_IL" : "en_US";

  const jsonLd: object[] = [];
  if (route.path === "/") {
    jsonLd.push(
      faqPageJsonLd(
        FAQ_ITEMS.map((i) => ({
          question: t.landing[i.questionKey],
          answer: t.landing[i.answerKey],
        })),
      ),
    );
    jsonLd.push({
      "@context": "https://schema.org",
      "@type": "HowTo",
      name: t.landing.howItWorksTitle,
      step: HOW_IT_WORKS_STEPS.map((s, idx) => ({
        "@type": "HowToStep",
        position: idx + 1,
        name: t.howItWorks.imageGenerator[s.titleKey],
        text: t.howItWorks.imageGenerator[s.descKey],
      })),
    });
  } else if (route.path === "/help") {
    jsonLd.push(
      faqPageJsonLd(
        HELP_FAQ_ITEMS.map((i) => ({
          question: t.help[i.questionKey],
          answer: t.help[i.answerKey],
        })),
      ),
    );
  } else if (route.path.startsWith("/compare/")) {
    // Comparison page — emit Article (page-as-published-content) + FAQPage
    // (the visible FAQ doubled as Q&A structured data). Article gives AI
    // engines the freshness signal (`dateModified`) and author attribution
    // that boosts Perplexity / Claude citation rate.
    const slug = route.path.replace("/compare/", "");
    const comparison = COMPARISONS[slug];
    if (comparison) {
      jsonLd.push({
        "@context": "https://schema.org",
        "@type": "Article",
        headline: comparison.title,
        description: comparison.metaDescription,
        author: { "@id": SITE.founderId },
        publisher: { "@id": SITE.orgId },
        datePublished: comparison.lastUpdated,
        dateModified: comparison.lastUpdated,
        mainEntityOfPage: url,
      });
      jsonLd.push(faqPageJsonLd(comparison.faqs));
    }
  } else if (route.path === "/pricing") {
    // One ItemList of Product entries — Google AI Overviews and Perplexity
    // both quote exact plan prices when this schema is present.
    jsonLd.push(pricingProductListJsonLd());
  } else if (FLOW_SEO_KEYS.has(route.seoKey)) {
    // Flow page (Image Generator, Talking Avatar, …) — register the per-tool
    // SoftwareApplication so AI engines can match the page to a specific app.
    jsonLd.push(
      softwareApplicationJsonLd({ name: title, description, url }),
    );
  }

  // Every non-home route gets a breadcrumb trail. Home is its own root —
  // breadcrumbs on it would be circular ("Home › Home").
  if (route.path !== "/") {
    jsonLd.push(breadcrumbJsonLd(title, url));
  }

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={PUBLIC_ROBOTS} />
      <link rel="canonical" href={url} />

      {/* hreflang — each language alternate points at its own prerendered URL
          (en → /pricing, he → /he/pricing). Google reads this to surface the
          right URL per searcher's locale. x-default sends crawlers without a
          stated language to the English version (the indexed default). */}
      <link rel="alternate" hrefLang="en" href={enUrl} />
      <link rel="alternate" hrefLang="he" href={heUrl} />
      <link rel="alternate" hrefLang="x-default" href={enUrl} />

      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:locale" content={ogLocale} />

      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />

      {jsonLd.map((data, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(data)}
        </script>
      ))}
    </Helmet>
  );
}

export default RouteSEO;
